import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';
import useAuthStore from '../stores/useAuthStore';
import axiosClient from '../services/api';

const GradingPage = () => {
  const { submissionId } = useParams();
  const { user } = useAuthStore();
  const toast = useToast();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [grade, setGrade] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        setLoading(true);
        const { data } = await axiosClient.get(`/learning/assignments/submissions/${submissionId}`);
        if (data.success) {
          setSubmission(data.data.submission);
          setAssignment(data.data.assignment);
          setGrade(data.data.submission.grade || '');
          setFeedback(data.data.submission.feedback || '');
        }
        else {
          toast.error('Failed to load submission');
        }
      } catch (err) {
        toast.error('Failed to load submission details');
      } finally {
        setLoading(false);
      }
    };

    if (submissionId) fetchSubmission();
  }, [submissionId]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!grade || isNaN(grade) || grade < 0 || grade > (assignment?.points || 100)) {
      toast.error(`Please enter a valid grade (0 - ${assignment?.points || 100})`);
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await axiosClient.put(`/learning/assignments/submissions/${submissionId}`, {
        grade: parseFloat(grade),
        feedback: feedback.trim(),
      });

      if (data.success) {
        toast.success('Assignment graded successfully!');
        navigate(-1)
      } else {
        toast.error(data.error || 'Failed to grade submission');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to grade submission');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Submissions
        </button>

        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Grade Assignment</h1>

        {submission && (
          <div className="space-y-8">
            {/* Assignment Info */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                {assignment?.title || 'Assignment'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Student</p>
                  <p className="font-medium">{submission.student?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Submitted</p>
                  <p className="font-medium">
                    {submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Points</p>
                  <p className="font-medium">{assignment?.points || 100} pts</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Status</p>
                  <p
                    className={`font-medium ${submission.gradedAt ? 'text-green-600' : 'text-warning-600'
                      }`}
                  >
                    {submission.gradedAt ? 'Graded' : 'Pending'}
                  </p>
                </div>
              </div>
            </div>

            {/* Submission Content */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Submission</h3>
              {submission.contentType === 'text' && (
                <div className="prose dark:prose-invert max-w-none">
                  <p>{submission.content || 'No content submitted.'}</p>
                </div>
              )}

              {submission.contentType === 'link' && (
                <div>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-2">Link:</p>
                  <a
                    href={submission.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 break-words"
                  >
                    {submission.content}
                  </a>
                </div>
              )}

              {submission.contentType === 'file' && submission.fileUrl && (
                <div>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-2">File:</p>
                  <a
                    href={submission.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download File
                  </a>
                </div>
              )}
            </div>

            {/* Grading Form */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Grade & Feedback</h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Grade *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={assignment?.points || 100}
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      Max: {assignment?.points || 100} points
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Feedback
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Provide constructive feedback..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isSubmitting ? 'Grading...' : 'Submit Grade'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GradingPage;
